import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useToasts } from "../hooks/useToasts";
import { useTheme } from "../hooks/useTheme";
import { useAuth } from "../hooks/useAuth";
import { profileAPI } from "../services/api";
import Button from "../components/Button";
import Form from "../components/Form";
import Prompt from "../components/Prompt";
import {
  Sun,
  Moon,
  Eye,
  EyeOff,
  AlertTriangle,
  ShieldOff,
  CheckCircle,
  XCircle,
  Clock,
  X as CloseIcon,
  MessageCircle,
  MessageCircleOff,
} from "lucide-react";
import Spinner from "../components/Spinner";
import "./Profile.css";

const Profile = () => {
  const { addToast } = useToasts();
  const { theme, toggleTheme } = useTheme();
  const { updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [showDisplayName, setShowDisplayName] = useState(true);
  const [toastsEnabled, setToastsEnabled] = useState(() => {
    const saved = localStorage.getItem("toastsEnabled");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [showDeactivatePrompt, setShowDeactivatePrompt] = useState(false);
  const [emailData, setEmailData] = useState({
    newEmail: "",
    currentPassword: "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [deleteData, setDeleteData] = useState({
    password: "",
    confirmation: "",
    understood: false,
  });
  const [deactivateData, setDeactivateData] = useState({
    password: "",
    understood: false,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await profileAPI.getProfile();
        const userData = response.data.user;
        setProfile(userData);
        setDisplayName(userData.displayName || "");
        // Check localStorage first, then server data
        const storedUser = localStorage.getItem("user");
        const localData = storedUser ? JSON.parse(storedUser) : {};
        const visibility =
          localData.showDisplayName !== undefined
            ? localData.showDisplayName
            : userData.showDisplayName !== false;
        setShowDisplayName(visibility);
      } catch (error) {
        console.error("Profile fetch error:", error);
        addToast("Failed to load profile", "error-warning");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [addToast]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    try {
      await profileAPI.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
        passwordData.confirmPassword,
      );
      addToast("Password changed successfully", "success-info");
      setShowPasswordForm(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to change password",
        "error-warning",
      );
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    try {
      const response = await profileAPI.changeEmailRequest(
        emailData.newEmail,
        emailData.currentPassword,
      );
      addToast(
        `Verification email sent to ${response.data.pendingEmail}`,
        "success-info",
      );
      setShowEmailForm(false);
      setEmailData({ newEmail: "", currentPassword: "" });
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to request email change",
        "error-warning",
      );
    }
  };

  const handleDeleteAccount = async () => {
    if (
      !deleteData.understood ||
      !deleteData.password ||
      deleteData.confirmation !== "delete"
    ) {
      addToast("Please complete all required fields", "error-warning");
      return;
    }

    try {
      await profileAPI.deleteAccount(
        deleteData.password,
        deleteData.confirmation,
      );
      addToast("Account deleted successfully", "success-info");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to delete account",
        "error-warning",
      );
    }
  };

  const handleDeactivateAccount = async () => {
    if (!deactivateData.understood || !deactivateData.password) {
      addToast("Please complete all required fields", "error-warning");
      return;
    }

    try {
      await profileAPI.deactivateAccount(deactivateData.password);
      addToast("Account self-deactivated successfully", "success-info");
      setShowDeactivatePrompt(false);
      setDeactivateData({ password: "", understood: false });
      // Force page refresh to ensure AuthContext has updated data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to deactivate account",
        "error-warning",
      );
    }
  };

  const handleReactivateAccount = async () => {
    try {
      await profileAPI.reactivateAccount();
      addToast("Account self-reactivated successfully", "success-info");
      // Force page refresh to ensure AuthContext has updated data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to reactivate account",
        "error-warning",
      );
    }
  };

  const handleExportData = async () => {
    try {
      addToast("Preparing data export...", "success-info");
      const response = await profileAPI.exportData();

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `asasiasa-data-export-${profile?.username || "user"}-${
        new Date().toISOString().split("T")[0]
      }.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addToast("Data exported successfully!", "success-info");
    } catch (error) {
      const errorMessage =
        error.response?.data instanceof Blob
          ? "Failed to export data"
          : error.response?.data?.details?.[0] || "Failed to export data";
      addToast(errorMessage, "error-warning");
    }
  };

  const handleInfoBubblesToggle = () => {
    const newValue = !toastsEnabled;
    setToastsEnabled(newValue);
    localStorage.setItem("toastsEnabled", JSON.stringify(newValue));
    addToast(
      newValue ? "Info bubbles ON" : "Info bubbles OFF",
      "success-info",
      3500,
      true,
    );
  };

  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);

    try {
      await logout();
      navigate("/", { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (loading) return <Spinner visible={true} />;

  return (
    <div className="profile-container">
      <div className="profile-header-row">
        <div className="profile-header-text">
          <h1 className="profile-title">Profile</h1>
        </div>
        <div className="profile-header-actions">
          <Button
            className="button-btn button-btn-blue profile-public-button"
            onClick={() => navigate("/public-profile")}
          >
            Public Profile
          </Button>
          <Button
            className="button-btn button-btn-red profile-logout-button"
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? "Logging out..." : "Logout"}
          </Button>
        </div>
      </div>

      {/* Show reactivation notice for deactivated users */}
      {profile?.isActive === false && profile?.deactivatedBy === "self" && (
        <div className="profile-alert profile-alert-self">
          <h3 className="profile-alert-title profile-alert-title-warning">
            <AlertTriangle size={20} /> Account Self-Deactivated
          </h3>
          <p className="profile-alert-text profile-alert-text-spaced">
            Your account is currently deactivated. You can only access your
            profile to reactivate, export your data, delete your account, or
            logout.
          </p>
          <button
            onClick={handleReactivateAccount}
            className="profile-reactivate-button"
          >
            Reactivate Account
          </button>
        </div>
      )}

      {/* Show admin deactivation notice */}
      {profile?.isActive === false && profile?.deactivatedBy === "admin" && (
        <div className="profile-alert profile-alert-admin">
          <h3 className="profile-alert-title profile-alert-title-error">
            <ShieldOff size={20} /> Account Deactivated by Admin
          </h3>
          <p className="profile-alert-text">
            Your account has been deactivated by an administrator. You can only
            access your profile to download your data or delete your account.
            Contact support for reactivation.
          </p>
        </div>
      )}

      <div className="profile-top-row">
        {/* Profile Information */}
        <div className="profile-section profile-info-section">
          <h2>Account Information</h2>
          <div className="profile-info">
            <div className="profile-field">
              <label>Email:</label>
              <span>{profile.email}</span>
            </div>
            <div className="profile-field">
              <label>Share Code:</label>
              <span className="profile-share-code">
                {profile.shareCode || "N/A"}
              </span>
            </div>
            <div className="profile-field">
              <label>Account Created:</label>
              <span>{new Date(profile.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="profile-field">
              <label>Last Login:</label>
              <span>
                {profile.lastLoginAt
                  ? new Date(profile.lastLoginAt).toLocaleString()
                  : "Never"}
              </span>
            </div>
            <div className="profile-field">
              <label>Role:</label>
              <span>{profile.role}</span>
            </div>
            {profile.role === "user" && (
              <div className="profile-field">
                <label>Total Articles:</label>
                <span>{profile.articleCount}</span>
              </div>
            )}
            <div className="profile-field">
              <label>Email Verified:</label>
              <span className="profile-verified-status">
                {profile.isEmailVerified ? (
                  <>
                    <CheckCircle size={16} color="green" /> Verified
                  </>
                ) : (
                  <>
                    <XCircle size={16} color="red" /> Not Verified
                  </>
                )}
              </span>
            </div>
            <div className="profile-field">
              <label>Plan:</label>
              <span className="profile-plan-row">
                {profile.tier === "early_access" ? "Early Access" : "Free"}
                <Button
                  className={`button-btn ${
                    profile.tier === "early_access"
                      ? "button-btn-blue profile-plan-btn"
                      : "button-btn-blue profile-plan-btn"
                  }`}
                  onClick={() => navigate("/plans")}
                >
                  {profile.tier === "early_access" ? "Manage" : "Upgrade"}
                </Button>
              </span>
            </div>
          </div>
        </div>

        {/* Editable Profile */}
        <div className="profile-section profile-settings-section">
          <h2>Profile Settings</h2>
          <div className="profile-form-group">
            <label>Display Name:</label>
            <div className="profile-display-name-wrapper">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onBlur={async (e) => {
                  try {
                    await profileAPI.updateProfile(e.target.value);
                    updateUser({ displayName: e.target.value });
                    setProfile((prev) => ({
                      ...prev,
                      displayName: e.target.value,
                    }));
                    addToast("Display name updated", "success-info");
                  } catch (error) {
                    console.error("Display name update error:", error);
                    addToast(
                      error.response?.data?.details?.[0] ||
                        "Failed to update display name",
                      "error-warning",
                    );
                  }
                }}
                maxLength={50}
                placeholder="Enter display name"
                className="profile-display-name-input"
              />
              <button
                type="button"
                onClick={async () => {
                  const newValue = !showDisplayName;
                  setShowDisplayName(newValue);
                  try {
                    await profileAPI.updateProfile(
                      undefined,
                      undefined,
                      newValue,
                    );
                    updateUser({ showDisplayName: newValue });
                    addToast(
                      newValue
                        ? "Display name shown in header"
                        : "Display name hidden from header",
                      "success-info",
                    );
                  } catch (error) {
                    console.error(
                      "Display name visibility update error:",
                      error,
                    );
                    addToast("Failed to update visibility", "error-warning");
                    setShowDisplayName(!newValue);
                  }
                }}
                className="profile-display-name-toggle"
                title={showDisplayName ? "Hide from header" : "Show in header"}
              >
                {showDisplayName ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
          </div>
          <div className="profile-form-group">
            <label>Theme:</label>
            <button
              type="button"
              onClick={toggleTheme}
              className="profile-theme-toggle"
            >
              {theme === "light" ? (
                <>
                  <Moon size={16} /> Switch to Dark
                </>
              ) : (
                <>
                  <Sun size={16} /> Switch to Light
                </>
              )}{" "}
              <span className="profile-theme-beta">*beta</span>
            </button>
          </div>
          <div className="profile-form-group">
            <label>Info Bubbles:</label>
            <button
              type="button"
              onClick={handleInfoBubblesToggle}
              className="profile-theme-toggle profile-info-bubbles-toggle"
              title={
                toastsEnabled ? "Info bubbles are ON" : "Turn info bubbles ON"
              }
            >
              {toastsEnabled ? (
                <>
                  <MessageCircle size={16} /> Info Bubbles ON
                </>
              ) : (
                <>
                  <MessageCircleOff size={16} /> Turn Info Bubbles ON
                </>
              )}
            </button>
            <p className="profile-info-bubbles-note">
              Recommended ON for confirmations and important study feedback.
            </p>
          </div>
        </div>

        {/* Security */}
        <div className="profile-section profile-security-section">
          <h2>Security</h2>
          <div className="profile-security-content">
            {!showPasswordForm && !showEmailForm ? (
              <>
                <div className="profile-delete-link">
                  <button
                    onClick={() => setShowPasswordForm(true)}
                    className="profile-blue-text-link"
                  >
                    Change Password
                  </button>
                </div>
                <div className="profile-delete-link">
                  <button
                    onClick={() => setShowEmailForm(true)}
                    className="profile-blue-text-link"
                  >
                    Change Email
                  </button>
                </div>
                <div className="profile-delete-link">
                  <button
                    onClick={() => setShowDeletePrompt(true)}
                    className="profile-delete-text-link"
                  >
                    Delete Account
                  </button>
                </div>
                {profile?.deactivatedBy !== "admin" && (
                  <div className="profile-delete-link">
                    {profile?.isActive === false &&
                    profile?.deactivatedBy === "self" ? (
                      <button
                        onClick={handleReactivateAccount}
                        className="profile-blue-text-link"
                      >
                        Reactivate Account
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowDeactivatePrompt(true)}
                        className="profile-gray-text-link"
                      >
                        Deactivate Account
                      </button>
                    )}
                  </div>
                )}
                <div className="profile-delete-link"></div>
              </>
            ) : showEmailForm ? (
              <form onSubmit={handleEmailChange}>
                <div className="profile-form-group">
                  <label>New Email Address:</label>
                  <input
                    type="email"
                    value={emailData.newEmail}
                    onChange={(e) =>
                      setEmailData({ ...emailData, newEmail: e.target.value })
                    }
                    required
                    placeholder="Enter new email address"
                  />
                </div>
                <div className="profile-form-group">
                  <label>Current Password:</label>
                  <input
                    type="password"
                    value={emailData.currentPassword}
                    onChange={(e) =>
                      setEmailData({
                        ...emailData,
                        currentPassword: e.target.value,
                      })
                    }
                    required
                    placeholder="Enter current password"
                  />
                </div>
                <div className="profile-form-buttons">
                  <Button type="submit" className="button-btn button-btn-blue">
                    Send Verification
                  </Button>
                  <Button
                    type="button"
                    className="button-btn"
                    onClick={() => setShowEmailForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handlePasswordChange}>
                <div className="profile-form-group">
                  <label>Current Password:</label>
                  <div className="profile-input-wrapper">
                    <input
                      type={showPassword.current ? "text" : "password"}
                      autoComplete="current-password"
                      value={passwordData.currentPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          currentPassword: e.target.value,
                        })
                      }
                      required
                      className={`profile-input ${
                        passwordData.currentPassword
                          ? "profile-input-actions"
                          : "profile-input-clear"
                      }`}
                    />
                    {passwordData.currentPassword && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setPasswordData({
                              ...passwordData,
                              currentPassword: "",
                            })
                          }
                          className="profile-input-btn profile-input-btn-clear"
                        >
                          <CloseIcon size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setShowPassword({
                              ...showPassword,
                              current: !showPassword.current,
                            })
                          }
                          className="profile-input-btn profile-input-btn-toggle"
                        >
                          {showPassword.current ? (
                            <EyeOff size={16} />
                          ) : (
                            <Eye size={16} />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="profile-form-group">
                  <label>New Password:</label>
                  <div className="profile-input-wrapper">
                    <input
                      type={showPassword.new ? "text" : "password"}
                      autoComplete="new-password"
                      value={passwordData.newPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          newPassword: e.target.value,
                        })
                      }
                      required
                      minLength={8}
                      className={`profile-input ${
                        passwordData.newPassword
                          ? "profile-input-actions"
                          : "profile-input-clear"
                      }`}
                    />
                    {passwordData.newPassword && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setPasswordData({
                              ...passwordData,
                              newPassword: "",
                            })
                          }
                          className="profile-input-btn profile-input-btn-clear"
                        >
                          <CloseIcon size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setShowPassword({
                              ...showPassword,
                              new: !showPassword.new,
                            })
                          }
                          className="profile-input-btn profile-input-btn-toggle"
                        >
                          {showPassword.new ? (
                            <EyeOff size={16} />
                          ) : (
                            <Eye size={16} />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="profile-form-group">
                  <label>Confirm New Password:</label>
                  <div className="profile-input-wrapper">
                    <input
                      type={showPassword.confirm ? "text" : "password"}
                      autoComplete="new-password"
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          confirmPassword: e.target.value,
                        })
                      }
                      required
                      className={`profile-input ${
                        passwordData.confirmPassword
                          ? "profile-input-actions"
                          : "profile-input-clear"
                      }`}
                    />
                    {passwordData.confirmPassword && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setPasswordData({
                              ...passwordData,
                              confirmPassword: "",
                            })
                          }
                          className="profile-input-btn profile-input-btn-clear"
                        >
                          <CloseIcon size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setShowPassword({
                              ...showPassword,
                              confirm: !showPassword.confirm,
                            })
                          }
                          className="profile-input-btn profile-input-btn-toggle"
                        >
                          {showPassword.confirm ? (
                            <EyeOff size={16} />
                          ) : (
                            <Eye size={16} />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="profile-form-buttons">
                  <Button type="submit" className="button-btn button-btn-blue">
                    Change Password
                  </Button>
                  <Button
                    type="button"
                    className="button-btn"
                    onClick={() => setShowPasswordForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
          {/* Privacy */}
          <h2 className="profile-privacy-title">Privacy</h2>
          <div className="profile-privacy-content">
            <div className="profile-delete-link">
              <button
                onClick={handleExportData}
                className="profile-blue-text-link"
              >
                Export My Data
              </button>
            </div>
            <div className="profile-delete-link">
              <Link to="/privacy" className="profile-blue-text-link">
                Privacy Policy
              </Link>
            </div>
            <div className="profile-delete-link">
              <Link to="/contact" className="profile-blue-text-link">
                Contact
              </Link>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div className="profile-section profile-permissions-section">
          <h2>Permissions</h2>
          <div className="profile-permissions-text">
            <p className="profile-permissions-intro">
              All permissions are self-service - no support required.
            </p>
            {profile.role === "user" && (
              <ul className="profile-permissions-list">
                <li>
                  <strong>Content Management:</strong> Create, edit, save, and
                  delete your own lessons, notes, and flashcards
                </li>
                <li>
                  <strong>Sharing & Communities:</strong> Create or join
                  communities, list communities in Discover, use community
                  codes, manage direct or approval join requests, share lessons,
                  accept invites, and quit joined communities
                </li>
                <li>
                  <strong>Public Profile:</strong> Edit your public name, bio,
                  interests, and visibility (public/private) for public profile,
                  community, and Meet People surfaces
                </li>
                <li>
                  <strong>Community Board:</strong> If you own a community, you
                  can publish and update board announcements for your members
                </li>
                <li>
                  <strong>Connections:</strong> Send private one to one notes,
                  request or accept connections, hide or unhide threads, and
                  disconnect accepted connections within your plan limits
                </li>
                <li>
                  <strong>Visibility & Study:</strong> Make lessons
                  public/private, generate share links, and study private,
                  public, shared, or community content
                </li>
                <li>
                  <strong>Account Management:</strong> Change password/email,
                  export your data as a TXT file,
                  self-deactivate/self-reactivate, and self-delete your account
                </li>
                <li>
                  <strong>Security Notices:</strong> Receive email notices for
                  password, email, deactivation, reactivation and deletion
                  changes
                </li>
                <li>
                  <strong>Plan Management:</strong> Upgrade to Early Access or
                  cancel back to Free at any time from your Profile or the Plans
                  page
                </li>
              </ul>
            )}
            {profile.role === "user" && (
              <div className="profile-terms-link">
                See more info in{" "}
                <Link
                  to="/terms"
                  className="profile-blue-text-link profile-terms-link-inline"
                >
                  Terms of Use
                </Link>
              </div>
            )}
            {profile.role === "admin" && (
              <ul className="profile-permissions-list">
                <li>
                  <strong>User Management:</strong> View all users, promote
                  users to admin, block/unblock users' login access,
                  deactivate/reactivate users' login access and articles, send
                  email verifications
                </li>
                <li>
                  <strong>Content Moderation:</strong> View all articles,
                  deactivate/reactivate articles, view articles edit history
                </li>
                <li>
                  <strong>System Access:</strong> Admin user management page,
                  content management page
                </li>
                <li>
                  <strong>Account Management:</strong> Change password/email,
                  self-deactivate/self-reactivate, self-delete admin account
                  (unless you are the last admin)
                </li>
                <li>
                  <strong>Security Notices:</strong> Receive email notices for
                  password, email, deactivation, reactivation and deletion
                  changes
                </li>
              </ul>
            )}
            {profile.role === "superadmin" && (
              <ul className="profile-permissions-list">
                <li>Promote admins to superadmins</li>
                <li>Block/unblock login access for admin accounts</li>
                <li>View admins and superadmins accounts</li>

                <li>
                  View articles as an unregistered user on the site's home page
                </li>
                <li>Change password and email</li>

                <li>
                  Self-deactivate and self-delete your account unless is the
                  last superadmin
                </li>
                <li>
                  Receive email notices for password, email, deactivation,
                  reactivation and deletion changes
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Deactivate Account Modal */}
      {showDeactivatePrompt && (
        <div
          className="profile-delete-overlay"
          onClick={() => setShowDeactivatePrompt(false)}
        >
          <div
            className="profile-delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Deactivate Account</h3>
            <p>Temporarily freeze and hide your account</p>
            <ul>
              <li>Your app access will be frozen until reactivation</li>
              <li>
                You can only use Profile to reactivate, export your data, delete
                your account, or logout
              </li>
              <li>Your public lessons, notes and flashcards are hidden</li>
              <li>Your public profile and Meet People visibility are hidden</li>
              <li>
                Communities you own are paused - hidden from Discover, join
                links and public pages, and members see a paused notice
              </li>
              <li>
                Community paused/reactivated notifications are sent to affected
                members
              </li>
              <li>
                Communities you are a member of show you as Self-deactivated in
                member lists
              </li>
              <li>
                Connections and conversations are preserved, but your one to one
                notes are paused while either member is deactivated
              </li>
              <li>All your content and relationships are preserved</li>
              <li>
                Active plan subscriptions are not paused or extended - your
                subscription cycle continues with no refund or recovery
              </li>
              <li>This is reversible - reactivation restores visibility</li>
            </ul>
            <div className="profile-delete-form">
              <label>
                <input
                  type="checkbox"
                  checked={deactivateData.understood}
                  onChange={(e) =>
                    setDeactivateData({
                      ...deactivateData,
                      understood: e.target.checked,
                    })
                  }
                />
                I understand my account will be frozen and hidden until
                reactivated
              </label>
              <div className="profile-form-group">
                <label>Password:</label>
                <input
                  type="password"
                  value={deactivateData.password}
                  onChange={(e) =>
                    setDeactivateData({
                      ...deactivateData,
                      password: e.target.value,
                    })
                  }
                  placeholder="Enter your password"
                />
              </div>
            </div>
            <div className="profile-delete-actions">
              <Button
                onClick={handleDeactivateAccount}
                className="button-btn profile-deactivate-button"
              >
                Deactivate Account
              </Button>
              <Button
                onClick={() => setShowDeactivatePrompt(false)}
                className="button-btn button-btn-green"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeletePrompt && (
        <div
          className="profile-delete-overlay"
          onClick={() => setShowDeletePrompt(false)}
        >
          <div
            className="profile-delete-modal profile-delete-account-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Delete Account</h3>
            <p>Delete Account - This action is IRREVERSIBLE!</p>
            {profile?.isActive === false &&
            profile?.deactivatedBy === "self" ? (
              <p className="profile-warning-box">
                <AlertTriangle size={16} className="profile-warning-icon" />
                <span>
                  <strong>Your account is already self-deactivated!</strong> No
                  need to delete unless you really want to permanently remove
                  everything.
                </span>
              </p>
            ) : (
              <p className="profile-warning-box">
                <AlertTriangle size={16} className="profile-warning-icon" />
                <span>
                  <strong>Consider deactivating first!</strong> Deactivation is
                  reversible and safer than permanent deletion.
                  <button
                    onClick={() => {
                      setShowDeletePrompt(false);
                      setShowDeactivatePrompt(true);
                    }}
                    className="profile-warning-action"
                  >
                    Deactivate Instead
                  </button>
                </span>
              </p>
            )}
            <ul className="profile-delete-account-list">
              <li>Deletes your account immediately</li>
              <li>
                Deletes all your lessons, notes, flashcards and saved content
              </li>
              <li>
                Deletes communities you own, including their memberships, shared
                lessons, community board content and community notifications
              </li>
              <li>Removes you from communities owned by other people</li>
              <li>
                Deletes your connections, connection requests and one to one
                conversations for everyone involved
              </li>
              <li>
                Removes your public profile from Meet People and public profile
                pages
              </li>
              <li>Cannot be recovered or undone</li>
              <li>Takes effect immediately and you will be logged out</li>
              <li>
                Consider exporting your data first from the Privacy section
              </li>
            </ul>
            <div className="profile-delete-form profile-delete-account-form">
              <label>
                <input
                  type="checkbox"
                  checked={deleteData.understood}
                  onChange={(e) =>
                    setDeleteData({
                      ...deleteData,
                      understood: e.target.checked,
                    })
                  }
                />
                I understand this action cannot be undone
              </label>
              <div className="profile-form-group">
                <label>Password:</label>
                <input
                  type="password"
                  value={deleteData.password}
                  onChange={(e) =>
                    setDeleteData({ ...deleteData, password: e.target.value })
                  }
                  placeholder="Enter your password"
                />
              </div>
              <div className="profile-form-group">
                <label>Type "delete" to confirm:</label>
                <input
                  type="text"
                  value={deleteData.confirmation}
                  onChange={(e) =>
                    setDeleteData({
                      ...deleteData,
                      confirmation: e.target.value,
                    })
                  }
                  placeholder="delete"
                />
              </div>
            </div>
            <div className="profile-delete-actions">
              <Button
                onClick={handleDeleteAccount}
                className="button-btn button-btn-red"
              >
                Delete Account
              </Button>
              <Button
                onClick={() => setShowDeletePrompt(false)}
                className="button-btn button-btn-green"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
